/**
 * MSI / Plessey check-digit algorithms.
 *
 * Four documented variants exist; retailers pick one (and don't always
 * agree). All operate on the data digits and yield 1 or 2 check digits
 * appended to the right.
 *
 * Mod 10:
 *   Right-to-left over the data digits, starting with weight 2 on the
 *   rightmost data digit. For each digit d * weight:
 *     - if product ≥ 10, add (product - 9) i.e. sum of its digits
 *     - else add product
 *   Weights alternate 2, 1, 2, 1, ... right to left.
 *   Check digit = (10 - sum % 10) % 10.
 *
 * Mod 11:
 *   Right-to-left over data digits, weights cycle 2..7 (then back to 2).
 *   sum = sum(d * weight). Check digit = (11 - sum % 11) % 11.
 *   Special case: when result is 10, conventions differ. We DROP that
 *   candidate (treat as no-match) rather than emit a non-digit.
 *
 * Mod 1010 (double Mod 10):
 *   Compute Mod-10 check digit C1, append it, then compute another
 *   Mod-10 check digit C2 over data+C1, append that too. Two trailing
 *   check digits total.
 *
 * Mod 1110 (Mod 11 then Mod 10):
 *   Mod-11 check on data → C1, then Mod-10 on data+C1 → C2.
 *
 * All four are pure functions; no I/O, no state. Trivial to unit test.
 */

import type { ChecksumVariant } from './types';

/**
 * Compute the Mod-10 check digit for a string of decimal digits.
 * @throws if `digits` contains non-digits.
 */
export function computeMod10(digits: string): number {
  assertDigits(digits);
  let sum = 0;
  // Right-to-left: rightmost gets weight 2, next 1, next 2, next 1, ...
  for (let i = 0; i < digits.length; i++) {
    const idxFromRight = i;
    const d = digits.charCodeAt(digits.length - 1 - idxFromRight) - 48;
    const weight = idxFromRight % 2 === 0 ? 2 : 1;
    const product = d * weight;
    sum += product >= 10 ? product - 9 : product;
  }
  return (10 - (sum % 10)) % 10;
}

/**
 * Compute the Mod-11 check digit. Returns null if the conventional
 * result is 10 (no single decimal digit available); the caller should
 * treat that candidate as "no match" rather than emit '10' or 'X'.
 */
export function computeMod11(digits: string): number | null {
  assertDigits(digits);
  let sum = 0;
  // Right-to-left: rightmost weight 2, then 3, 4, 5, 6, 7, back to 2, ...
  for (let i = 0; i < digits.length; i++) {
    const d = digits.charCodeAt(digits.length - 1 - i) - 48;
    const weight = ((i % 6) + 2); // cycles 2..7
    sum += d * weight;
  }
  const rem = sum % 11;
  const check = (11 - rem) % 11;
  return check === 10 ? null : check;
}

/**
 * Given a candidate string of digits, try to interpret it as
 * data + check digit(s) for the given variant. Returns the DATA portion
 * (without the check digit) on success, or null on failure.
 *
 * This is the function the decoder uses: given raw decoded digits and a
 * variant hypothesis, did the trailing digits validate? If yes, return
 * just the data; if no, this hypothesis is wrong.
 */
export function validateAndStrip(raw: string, variant: ChecksumVariant): string | null {
  assertDigits(raw);
  switch (variant) {
    case 'none':
      return raw;
    case 'mod10': {
      if (raw.length < 2) return null;
      const data = raw.slice(0, -1);
      const claimed = raw.charCodeAt(raw.length - 1) - 48;
      return computeMod10(data) === claimed ? data : null;
    }
    case 'mod11': {
      if (raw.length < 2) return null;
      const data = raw.slice(0, -1);
      const claimed = raw.charCodeAt(raw.length - 1) - 48;
      const expected = computeMod11(data);
      return expected !== null && expected === claimed ? data : null;
    }
    case 'mod1010': {
      if (raw.length < 3) return null;
      const data = raw.slice(0, -2);
      const c1Claimed = raw.charCodeAt(raw.length - 2) - 48;
      const c2Claimed = raw.charCodeAt(raw.length - 1) - 48;
      const c1 = computeMod10(data);
      if (c1 !== c1Claimed) return null;
      const c2 = computeMod10(data + c1);
      return c2 === c2Claimed ? data : null;
    }
    case 'mod1110': {
      if (raw.length < 3) return null;
      const data = raw.slice(0, -2);
      const c1Claimed = raw.charCodeAt(raw.length - 2) - 48;
      const c2Claimed = raw.charCodeAt(raw.length - 1) - 48;
      const c1 = computeMod11(data);
      if (c1 === null || c1 !== c1Claimed) return null;
      const c2 = computeMod10(data + c1);
      return c2 === c2Claimed ? data : null;
    }
  }
}

/**
 * Append the appropriate check digit(s) to data and return the full
 * encoded string. Used by the encoder to round-trip-test the decoder.
 * Returns null if the variant can't produce a check digit (e.g. Mod-11
 * landed on the disallowed 10 value).
 */
export function appendChecksum(data: string, variant: ChecksumVariant): string | null {
  assertDigits(data);
  switch (variant) {
    case 'none':
      return data;
    case 'mod10':
      return data + computeMod10(data);
    case 'mod11': {
      const c = computeMod11(data);
      return c === null ? null : data + c;
    }
    case 'mod1010': {
      const c1 = computeMod10(data);
      const c2 = computeMod10(data + c1);
      return data + c1 + c2;
    }
    case 'mod1110': {
      const c1 = computeMod11(data);
      if (c1 === null) return null;
      const c2 = computeMod10(data + c1);
      return data + c1 + c2;
    }
  }
}

function assertDigits(s: string): void {
  if (!/^[0-9]*$/.test(s)) {
    throw new TypeError(`MSI checksum input must be decimal digits only, got: ${s}`);
  }
}
